import { Link } from "react-router-dom";
import { ArrowRight, BookHeart, Sprout, Sunrise, Trophy, MapPin, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteSettings, pickText } from "@/lib/siteSettings";

const themes = [
  {
    icon: Sunrise,
    title: "Mornings & Rhythms",
    blurb: "How the first hour of your day shapes everything that follows.",
    to: "/lifestyles/mornings-and-rhythms",
  },
  {
    icon: BookHeart,
    title: "Family & Story",
    blurb: "Breaking inherited cycles and building a family culture you're proud of.",
    to: "/book",
  },
  {
    icon: Sprout,
    title: "Grow your mind",
    blurb: "Habits and the unglamorous daily practices that compound over years.",
    to: "/lifestyles/grow-your-mind",
  },
];

const DRESSAGE_IMG =
  "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Lifestyles() {
  const { settings } = useSiteSettings();
  return (
    <div data-testid="lifestyles-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 grid md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">Lifestyles</span>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]">
            A life
            <br />
            <span className="italic text-primary">by design.</span>
          </h1>
        </div>
      </section>

      {/* Follow Tammy on Facebook */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16" data-testid="follow-facebook">
        <a
          href={pickText(settings, "links.facebookPage", "https://www.facebook.com/tammyricharKW")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="follow-facebook-link"
          className="group block bg-[#0F1E33] text-[#F5F8FB] rounded-2xl overflow-hidden card-lift"
        >
          <div className="grid md:grid-cols-12 gap-6 items-center p-7 md:p-9">
            <div className="md:col-span-2 flex md:justify-start">
              <div className="w-14 h-14 rounded-2xl bg-[#93C5FD]/20 flex items-center justify-center">
                <Facebook className="text-[#93C5FD]" size={26} strokeWidth={1.6} />
              </div>
            </div>
            <div className="md:col-span-7">
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#93C5FD]">
                Stay in the loop
              </span>
              <h2 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">
                Follow Tammy on Facebook.
              </h2>
              <p className="text-sm md:text-base opacity-85 mt-2 leading-relaxed max-w-xl">
                Real-life notes on business, family, and the everyday
                practices behind a life by design — straight from Tammy's
                business page.
              </p>
            </div>
            <div className="md:col-span-3 md:text-right">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[#93C5FD] group-hover:gap-3 transition-all">
                Open Facebook page <ArrowRight size={16} />
              </span>
            </div>
          </div>
        </a>
      </section>

      {/* Themes */}
      <section className="bg-muted/40 py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {themes.map((t, i) => {
              const inner = (
                <>
                  <div className="flex items-start justify-between">
                    <t.icon className="text-primary" size={26} strokeWidth={1.5} />
                    {t.to && (
                      <ArrowRight
                        className="text-primary/0 group-hover:text-primary transition-all"
                        size={16}
                      />
                    )}
                  </div>
                  <h3 className="font-serif text-xl mt-5">{t.title}</h3>
                  <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{t.blurb}</p>
                  {t.to && (
                    <span className="inline-flex items-center gap-1.5 mt-4 text-xs uppercase tracking-[0.18em] text-primary font-medium">
                      Explore
                    </span>
                  )}
                </>
              );

              if (t.to) {
                return (
                  <Link
                    key={t.title}
                    to={t.to}
                    data-testid={`lifestyle-theme-${i}`}
                    className="group bg-card border border-border rounded-2xl p-8 card-lift block"
                  >
                    {inner}
                  </Link>
                );
              }
              return (
                <div
                  key={t.title}
                  className="bg-card border border-border rounded-2xl p-8"
                  data-testid={`lifestyle-theme-${i}`}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Dressage section */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="dressage-section">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Trophy size={14} /> Dressage & Wellington
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              The lessons we learn
              <br />
              <span className="italic text-primary">from the ring.</span>
            </h2>
            <div className="mt-8 space-y-5 text-lg text-foreground/85 leading-relaxed">
              <p>
                Tammy's granddaughter competes in dressage in Wellington, and
                the sport has become a quiet thread through their family
                life — a place of patience, partnership, and the kind of
                steady work that adds up over years.
              </p>
              <p>
                Anyone who has stood at the rail through a long warm-up
                knows: the connection between rider and horse is the same
                connection we're all building, in different forms — between
                ourselves and the lives we're trying to live well.
              </p>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-foreground/65">
              <MapPin size={14} className="text-primary" />
              Winters spent in Wellington, FL · Summers in Maine
            </div>
          </div>

          <div className="md:col-span-6 order-1 md:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 to-secondary/20 rounded-3xl rotate-[-2deg]" />
              <img
                src={DRESSAGE_IMG}
                alt="Dressage ring — Wellington"
                className="relative rounded-2xl w-full aspect-[4/5] object-cover"
                data-testid="dressage-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif text-3xl md:text-4xl leading-tight">
          Want these in your inbox?
        </h2>
        <p className="text-foreground/75 mt-4">
          Tammy sends one slow, thoughtful note a month. No promotion, no fluff.
        </p>
        <Link to="/contact" data-testid="lifestyle-cta-contact">
          <Button className="mt-8 rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8">
            Get in touch
          </Button>
        </Link>
      </section>
    </div>
  );
}
